import {
  Avatar,
  Box,
  Container,
  IconButton as MuiIconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { IconButton, Icons, MenuDrawer, MenuListItem } from 'utoniq-ui'

import {
  CurrentUserFragment,
  FollowArtistsFragment,
  Maybe,
  MemberArtistFragment,
} from '@/clients/utoniq-core/schema'
import { AppThemeProvider, styles, styleSets } from '@/components/styles'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { individualHideWallet } from '@/core/individual'
import { useAnalytics } from '@/lib/analytics'
import { filterHiddenItem } from '@/lib/array'
import { usePrivateAction } from '@/lib/privateAction'

export type SideMenuProps = {
  followArtists: Maybe<FollowArtistsFragment>
  memberArtists: Maybe<MemberArtistFragment[]>
  open: boolean
  user: Maybe<CurrentUserFragment>
  onClose: () => void
  isArtist: boolean | null
  responsive?: boolean
  maxWidth?: number
  enableDmRoom?: boolean
}

export const SideMenu: Organism<SideMenuProps> = ({
  open = false,
  followArtists,
  memberArtists,
  user = null,
  isArtist = false,
  responsive = false,
  onClose,
  maxWidth = 600,
  enableDmRoom = false,
}) => {
  const { handleLoginModal } = usePrivateAction()
  const { t } = useTranslation('sideMenu')
  const { routerPages, generatePath, router } = useRouter()
  const { analytics } = useAnalytics()
  const { user: currentUser } = useCurrentUser()

  const listService = useMemo(
    () => [
      {
        title: t('home'),
        IconButton: (
          <IconButton.Home
            bgcolor="icon.background"
            href={routerPages.GlobalHome}
          />
        ),
      },
      {
        title: t('wallet'),
        hidden: !config.modules.prepaidPoint || individualHideWallet(),
        IconButton: (
          <IconButton.Wallet
            href={routerPages.Wallet}
            onClick={handleLoginModal()}
            bgcolor="icon.background"
          />
        ),
      },
      {
        title: t('ownedItems'),
        IconButton: (
          <IconButton.OwnedItems
            href={routerPages.MyOwnedItems}
            onClick={handleLoginModal()}
            bgcolor="icon.background"
          />
        ),
      },
      {
        title: t('pickup'),
        hidden: !config.modules.pickup,
        IconButton: (
          <MuiIconButton
            href={routerPages.MyOwnedTicketsEventSelection}
            onClick={(() => {
              analytics?.track('click_eventPickupList', {
                applicationKey: config.applicationKey,
                type: 'sideMenu',
              })
              return handleLoginModal()
            })()}
            sx={{ bgcolor: 'grey.50' }}
          >
            <Icons.PickUp sx={{ color: 'grey.600' }} />
          </MuiIconButton>
        ),
      },
      {
        title: t('cart'),
        hidden: !config.modules.cart,
        IconButton: (
          <MuiIconButton
            href={routerPages.StoreCartList}
            onClick={(() => {
              analytics?.track('click_cart', {
                applicationKey: config.applicationKey,
                type: 'sideMenu',
                isAnonymous: currentUser?.isAnonymous,
              })
              // カートには匿名ログイン状態でもアクセスできる
              return currentUser ? () => {} : handleLoginModal()
            })()}
            sx={{ bgcolor: 'grey.50' }}
          >
            <Icons.Store sx={{ color: 'grey.600' }} />
          </MuiIconButton>
        ),
      },
      {
        title: t('tradeRequest'),
        hidden: !config.modules.trade,
        IconButton: (
          <IconButton.Market
            href={routerPages.MyTrade}
            onClick={handleLoginModal()}
            bgcolor="icon.background"
          />
        ),
      },
      {
        title: t('dmRooms'),
        hidden: !enableDmRoom,
        IconButton: (
          <IconButton.DM
            href={routerPages.DmRooms}
            sx={{ color: 'grey.700' }}
            onClick={handleLoginModal()}
            bgcolor="icon.background"
          />
        ),
      },
      {
        title: t('manager'),
        IconButton: (
          <IconButton.Management
            href={routerPages.Management}
            onClick={handleLoginModal()}
            bgcolor="icon.background"
            badge={
              user?.hasUnappliedDeliveryItems ||
              user?.hasWaitingPaymentItems ||
              user?.hasWaitingResaleRafflePaymentItems ||
              user?.isMembershipPersonalInformationRequired
                ? {
                    badgeContent: '',
                    color: 'like' as any,
                    sx: {
                      '& > span': {
                        top: -4,
                        right: -4,
                        minWidth: 12,
                        height: 12,
                        bgcolor: 'like.tap',
                      },
                    },
                  }
                : undefined
            }
          />
        ),
      },
      {
        title: t('setting'),
        IconButton: (
          <IconButton.Setting
            href={routerPages.Setting}
            color="text.secondary"
            onClick={handleLoginModal()}
            bgcolor="icon.background"
            badge={
              user?.isMembershipPersonalInformationRequired
                ? {
                    badgeContent: '',
                    color: 'like' as any,
                    sx: {
                      '& > span': {
                        top: -4,
                        right: -4,
                        minWidth: 12,
                        height: 12,
                        bgcolor: 'like.tap',
                      },
                    },
                  }
                : undefined
            }
          />
        ),
      },
      {
        title: t('notification'),
        IconButton: (
          <IconButton.Notice
            href={routerPages.Notifications}
            onClick={handleLoginModal()}
            color="text.secondary"
            bgcolor="icon.background"
            badge={
              user?.unreadNotificationCount
                ? {
                    badgeContent: '',
                    color: 'like' as any,
                    sx: {
                      '& > span': {
                        top: -4,
                        right: -4,
                        minWidth: 12,
                        height: 12,
                        bgcolor: 'like.tap',
                      },
                    },
                  }
                : undefined
            }
          />
        ),
      },
      {
        title: t('console'),
        hidden: !isArtist,
        IconButton: (
          <IconButton.Artist
            href={routerPages.ConsoleDashboard}
            onClick={handleLoginModal()}
            bgcolor="icon.background"
          />
        ),
      },
    ],
    [
      handleLoginModal,
      isArtist,
      enableDmRoom,
      routerPages.ConsoleDashboard,
      routerPages.DmRooms,
      routerPages.GlobalHome,
      routerPages.Management,
      routerPages.MyOwnedItems,
      routerPages.MyTrade,
      routerPages.Notifications,
      routerPages.Setting,
      routerPages.Wallet,
      t,
      user?.hasUnappliedDeliveryItems,
      user?.hasWaitingPaymentItems,
      user?.unreadNotificationCount,
    ]
  )

  return (
    <AppThemeProvider>
      <MenuDrawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          maxWidth: responsive ? '100%' : maxWidth,
          width: '100%',
          margin: '0 auto',
        }}
        PaperProps={{
          sx: {
            pt: styles.safeAreaTop,
            pb: styles.safeAreaBottom,
            bgcolor: 'background.default',
            backgroundImage: 'none',
            left: 'initial',
          },
        }}
      >
        <Container
          disableGutters
          sx={{
            width: '280px',
            bgcolor: 'background.default',
            height: '100%',
          }}
        >
          <Stack sx={{ height: '100%' }}>
            <Stack>
              <ListItem
                disablePadding
                sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}
              >
                <ListItemButton
                  sx={{ height: 76, px: 5 }}
                  href={!currentUser ? routerPages.Top : routerPages.MyProfile}
                >
                  {!currentUser?.isSigned ? (
                    <>
                      <ListItemIcon>
                        <Icons.MemberShip fontSize="medium" sx={{ mr: 3 }} />
                      </ListItemIcon>
                      <ListItemText
                        slotProps={{ primary: { variant: 'subtitle1' } }}
                        primary={t('newRegisterOrLogin')}
                      />
                    </>
                  ) : (
                    <>
                      <ListItemAvatar>
                        <Avatar
                          src={
                            user?.avatar?.mediumUri ||
                            currentUser?.avatarUrl ||
                            ''
                          }
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={user?.displayName || currentUser?.displayName}
                      />
                    </>
                  )}
                  <ListItemIcon>
                    <Icons.Next fontSize="medium" />
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
              {(user?.hasUnappliedDeliveryItems ||
                user?.hasWaitingPaymentItems ||
                user?.hasWaitingResaleRafflePaymentItems ||
                user?.isMembershipPersonalInformationRequired) && (
                <Paper
                  elevation={3}
                  sx={{ borderRadius: 0, pb: 'unset !important' }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    px={4}
                    py={3}
                    component={Link}
                    href={routerPages.Management}
                  >
                    <Icons.Info fontSize="small" sx={{ color: 'like.tap' }} />
                    <Typography variant="subtitle2" color="like.tap">
                      {t(
                        'managementNotification',
                        '確認が必要な項目があります。'
                      )}
                    </Typography>
                  </Stack>
                </Paper>
              )}
              {currentUser && (
                <Container
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    px: 5,
                    py: 3,
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                  }}
                >
                  {filterHiddenItem(listService).map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pl: { xs: 1, sm: 1 },
                        pr: { xs: 1, sm: 2 },
                        color: 'grey.400',
                      }}
                    >
                      {item.IconButton}
                      <Typography
                        variant="overline"
                        sx={{
                          color: 'text.secondary',
                          textAlign: 'center',
                          mt: 2,
                          height: '44px',
                          width: '51px',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                  ))}
                </Container>
              )}
              {config.modules.membership && !!memberArtists?.length && (
                <CellFrame
                  sx={{
                    py: 3,
                    pb: 6,
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'grey.600', py: 2, pb: 3, px: 5 }}
                  >
                    {t('artistMember')}
                  </Typography>
                  {memberArtists.map(({ artist }) => (
                    <MenuListItem
                      key={artist.id}
                      sx={{
                        backgroundColor: 'grey.50',
                        height: 56,
                        px: 5,
                        py: 0,
                      }}
                      title={artist.displayName || ''}
                      icon={
                        <Avatar
                          src={artist.avatar?.smallUri || ''}
                          sx={{ width: 28, height: 28 }}
                        />
                      }
                      href={generatePath(routerPages.ArtistTop, {
                        artistId: artist.id,
                      })}
                      onClick={() => onClose()}
                      primaryTypographyProps={{
                        variant: 'body1',
                        color: 'text.primary',
                      }}
                    />
                  ))}
                </CellFrame>
              )}

              {!!followArtists?.edges?.length && (
                <CellFrame sx={{ py: 3, pb: 6 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'grey.600', py: 2, pb: 3, px: 5 }}
                  >
                    {t('artistFollowing')}
                  </Typography>
                  {followArtists.edges.map(({ node }) => (
                    <MenuListItem
                      key={node.artist.id}
                      sx={{
                        backgroundColor: 'grey.50',
                        height: 56,
                        px: 5,
                        py: 0,
                        borderBottom: '1px solid',
                        borderColor: 'grey.100',
                        '.css-tlelie-MuiListItemText-root': {
                          width: 210,
                        },
                      }}
                      title={node.artist.displayName}
                      icon={
                        <Avatar
                          src={node.artist.avatar?.smallUri || ''}
                          sx={{ width: 28, height: 28 }}
                        />
                      }
                      href={generatePath(routerPages.ArtistTop, {
                        artistId: node.artist.id,
                      })}
                      onClick={() => onClose()}
                      primaryTypographyProps={{
                        variant: 'body1',
                        color: 'text.primary',
                        sx: {
                          mr: 8,
                          ...styleSets.lineCamp(1),
                        },
                      }}
                      secondaryAction={
                        config.modules.membership ? (
                          <IconButton.Thread
                            badge={{
                              badgeContent: node.artist.totalUnreadMentionCount,
                              sx: {
                                span: {
                                  bgcolor: 'like.tap',
                                  color: 'white',
                                  minWidth: 16,
                                  width: 16,
                                  height: 16,
                                  fontSize: '8px',
                                  lineHeight: '16px',
                                  top: 4,
                                  right: 4,
                                  zIndex: 3,
                                  fontWeight: 'bold',
                                },
                              },
                            }}
                            sx={{ position: 'relative', zIndex: 2 }}
                            onClick={event => {
                              event.preventDefault()
                              event.stopPropagation()
                              onClose()
                              router.push(
                                generatePath(routerPages.ArtistThreads, {
                                  artistId: node.artist.id,
                                })
                              )
                            }}
                          />
                        ) : null
                      }
                    />
                  ))}
                </CellFrame>
              )}
            </Stack>
            <Stack flexGrow={1} />
            {/*
              @todo
              ユーザーからアーティストへの登録導線
              API実装後に表示する
              https://zpl.io/b6K6ZJd
             */}
            {/* {!userClaims?.orgs?.[config.organizationId]?.artist && (
              <Stack>
                <MenuListItem
                  sx={{
                    backgroundColor: 'grey.50',
                    py: 3,
                    px: 5,
                    height: 'auto',
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                  }}
                  title={t('artistRegistration')}
                  icon={<Icons.Avatar />}
                  href={routerPages.SignUpArtist}
                  onClick={() => onClose()}
                  primaryTypographyProps={{
                    variant: 'body1',
                    color: 'text.primary',
                  }}
                />
              </Stack>
            )} */}
          </Stack>
        </Container>
      </MenuDrawer>
    </AppThemeProvider>
  )
}
